import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { defaults, find, omit, some } from 'lodash';

import { ContactProductAssignmentComponent } from '@lockton-apps/contacts/ui/contact-product-assignment';
import { MaterialModule } from '@lockton-apps/material';
import {
  Client,
  Contact,
  ContactCreateUpdate,
  Policy,
} from '@lockton-apps/shared/interfaces';
import { UiModule, slideInTopOpenClose } from '@lockton-apps/shared/ui/lui';
import {
  FormGroupValidationDirective,
  provideAdditionalValidationMessages,
} from '@lockton-apps/shared/util/form-utils';
import { SUPPORTED_LOCALES } from '@lockton-apps/shared/util/localization';
import { ControlsOf } from '@lockton-apps/shared/util/utility-types';
import { ValidatorsService } from '@lockton-apps/shared/data-access/validator-services';

interface ContactModel {
  email: string;
  givenName: string;
  surname: string;
  language: string;
  isPrimary: boolean;
  phoneNumber: string;
  policyIds: number[] | null;
}

@Component({
    selector: 'la-contact-form',
    imports: [
        CommonModule,
        UiModule,
        MaterialModule,
        ReactiveFormsModule,
        FormGroupValidationDirective,
        ContactProductAssignmentComponent,
    ],
    templateUrl: './contact-form.component.html',
    styleUrls: ['./contact-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        provideAdditionalValidationMessages({
            phoneNumber: {
                mask: $localize `Please enter a valid phone number`,
            },
            email: {
                emailExists: $localize `The email you have entered already exists`,
            },
        }),
    ],
    animations: [slideInTopOpenClose]
})
export class ContactFormComponent {
  @ViewChild(FormGroupDirective) ngForm!: FormGroupDirective;
  @ViewChild(ContactProductAssignmentComponent)
  policyAssignmentComponent!: ContactProductAssignmentComponent;

  @Input() showClientIdControl = true;
  @Input() showAddProducts = true;
  @Input() clientId!: number | undefined;

  @Input() get model() {
    return this._model;
  }
  set model(value: Contact | undefined) {
    this._model = value;

    if (value && this.form) {
      this.form.patchValue(this.toModel(value));
    }
  }
  _model!: Contact | undefined;

  availableLanguages = SUPPORTED_LOCALES;

  @Input() showLanguageControl = true;

  @Input() clients!: Client[];

  @Input() clientsLoaded!: boolean;

  @Input() policies!: Policy[];

  @Input() policiesLoaded!: boolean;

  @Input() loaded!: boolean;

  @Output() submitForm = new EventEmitter<ContactCreateUpdate>();

  form!: FormGroup<ControlsOf<ContactModel>>;

  get isPrimaryContact() {
    const control = this.form.get('isPrimary');
    return control ? control.value : false;
  }

  get policyIdsControl() {
    return this.form.get('policyIds') as AbstractControl<number[] | null>;
  }

  get emailControl() {
    return this.form.get('email');
  }

  constructor(
    private fb: FormBuilder,
    private validatorService: ValidatorsService
  ) {
    this._buildForm();
  }

  clientDisplayWith(clientId: number): string {
    const match = find(this.clients, (client) => client.id === clientId);
    return match ? match.clientData.insuredName : '';
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    this.policyAssignmentComponent &&
      this.policyAssignmentComponent.assignSelectedPolicies();

    if (!this.form.valid) {
      return;
    }

    this.submitForm.emit(this.toContact(this.form.getRawValue()));
  }

  private _buildForm(): void {
    this.form = this.fb.nonNullable.group({
      language: this.fb.control(this.availableLanguages[0].code, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      givenName: this.fb.control('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      surname: this.fb.control('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      email: this.fb.control('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      isPrimary: this.fb.control<boolean>(false, { nonNullable: true }),
      phoneNumber: this.fb.control('', {
        nonNullable: true,
      }),
      policyIds: this.fb.control<number[] | null>([], {
        nonNullable: true,
      }),
    });
  }

  private toModel(contact: Contact): ContactModel {
    return omit(
      {
        ...contact,
        isPrimary: some(contact.clients, {
          isPrimary: true,
          id: this.clientId,
        }),
      },
      ['userId', 'displayName', 'enabled']
    );
  }

  private toContact(model: ContactModel): ContactCreateUpdate {
    const { givenName, surname, policyIds } = model;

    return defaults(
      {
        clientId: this.clientId,
        displayName: `${givenName} ${surname}`,
        policyIds: policyIds || [],
      },
      model
    );
  }
}
